/**
 * The "this will take a while — go ahead?" prompt.
 *
 * A turn the backend gates on cost ends with `conversation.confirmation_required`
 * and nothing has run. Without something to answer it, that turn is a dead
 * end: the question is stuck and the only way forward is retyping it.
 *
 * Answering starts a **new** turn — the original never ran, so there is
 * nothing to resume.
 */

import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAssistantAbly } from "../../contexts/AIAssistantAblyContext";
import { useScreenContext } from "../../hooks/useScreenContext";
import { confirmDeepResearch } from "../../state/conversationActions";
import "./ConfirmationPrompt.scss";

const seconds = (value) => {
  if (!value) return null;
  return value < 60 ? `${Math.round(value)}s` : `${Math.round(value / 60)} min`;
};

const ConfirmationPrompt = () => {
  const dispatch = useDispatch();
  const { conversationId } = useAssistantAbly();
  const { buildCurrentScreenContext } = useScreenContext();
  const pending = useSelector((state) => state?.chat?.pendingConfirmation ?? null);
  const messages = useSelector((state) => state?.chat?.messages ?? []);

  if (!pending) return null;

  const estimate = pending.estimate || {};
  const duration = seconds(estimate.estimated_seconds);

  // The question that was gated, so it can be sent again as-is.
  const asked = messages.find(
    (message) => message.turn_id === pending.turnId && message.type === "You",
  );

  const goAhead = () => {
    if (!asked?.text) return;
    dispatch(
      confirmDeepResearch({
        conversationId,
        text: asked.text,
        screenContext: buildCurrentScreenContext(),
      }),
    );
  };

  return (
    <div className="lou-confirm" role="status">
      <p className="lou-confirm__message">
        {pending.message || "This one needs a deeper look before I can answer."}
      </p>

      {(duration || estimate.estimated_tool_calls) && (
        <p className="lou-confirm__estimate">
          {duration ? `About ${duration}` : null}
          {duration && estimate.estimated_tool_calls ? ", " : null}
          {estimate.estimated_tool_calls
            ? `${estimate.estimated_tool_calls} lookups`
            : null}
          {estimate.reason ? ` — ${estimate.reason}` : null}
        </p>
      )}

      <div className="lou-confirm__actions">
        <button type="button" className="lou-confirm__go" onClick={goAhead}>
          Go ahead
        </button>
      </div>
    </div>
  );
};

export default ConfirmationPrompt;
